import React, { useEffect, useRef, useState, useContext } from "react";
import UserContext from "../context/userContext";
import {
  drawBrush,
  drawRect,
  drawCircle,
  drawTriangle,
  drawLine,
  downloadClick,
  drawEraser,
} from "../utils/drawingAction";
import DownBtn from "../assets/image/down-btn.png";
import Snapshot from "../utils/Snapshot";
import CustomCursor from "./CustomCursor";
import image_1 from "../assets/templates/image_1.png";
import image_2 from "../assets/templates/image_2.png";
import image_3 from "../assets/templates/image_3.png";

const snapshotImage = new Snapshot();

function DrawingArea() {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const [isPushBlank, setIsPushBlank] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lineOpacity] = useState(1);
  const [blankPanel, setBlankPanel] = useState(true);
  const [prevMouseX, setPrevMouseX] = useState(0);
  const [prevMouseY, setPrevMouseY] = useState(0);

  const {
    currentColor,
    currentSize,
    clear,
    setClear,
    currentTool,
    areaFocus,
    setAreaFocus,
    setCurrentMouseX,
    setCurrentMouseY,
    backgroundImage,
  } = useContext(UserContext);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (blankPanel) {
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      setBlankPanel(false);
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalAlpha = lineOpacity;
    ctx.strokeStyle = currentColor;
    ctx.lineWidth = currentSize;
    ctxRef.current = ctx;

    if (clear) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      snapshotImage.clear();
      setIsPushBlank(true);
      setClear(false);
    }
  }, [currentColor, lineOpacity, currentSize, clear, blankPanel, setClear]);

  useEffect(() => {
    const canvas = canvasRef.current;
    ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
    snapshotImage.clear();
    const image = new Image();
    switch (backgroundImage) {
      case "image_1":
        image.src = image_1;
        break;
      case "image_2":
        image.src = image_2;
        break;
      case "image_3":
        image.src = image_3;
        break;
      default:
        break;
    }
    ctxRef.current.drawImage(
      image,
      canvas.width / 2 - 150,
      canvas.height / 2 - 150,
      300,
      300
    );
  }, [backgroundImage]);

  const getCanvasOffset = (canvas) => {
    const rect = canvas.getBoundingClientRect();
    return {
      offsetX: rect.left,
      offsetY: rect.top,
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const { offsetX, offsetY } = getCanvasOffset(canvas);
    let x, y;
    if (e.type === "mousedown") {
      x = e.nativeEvent.offsetX;
      y = e.nativeEvent.offsetY;
    } else if (e.type === "touchstart") {
      x = e.nativeEvent.touches[0].clientX - offsetX;
      y = e.nativeEvent.touches[0].clientY - offsetY;
    }
    if (isPushBlank) {
      snapshotImage.addSnapshot(
        ctxRef.current.getImageData(
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        )
      );
      setIsPushBlank(false);
    }

    setIsDrawing(true);
    setPrevMouseX(x);
    setPrevMouseY(y);
    ctxRef.current.beginPath();
  };

  const draw = (e) => {
    const canvas = canvasRef.current;
    const { offsetX, offsetY } = getCanvasOffset(canvas);
    let x, y;
    if (e.type === "mousemove") {
      x = e.nativeEvent.offsetX;
      y = e.nativeEvent.offsetY;
    } else if (e.type === "touchmove") {
      x = e.nativeEvent.touches[0].clientX - offsetX;
      y = e.nativeEvent.touches[0].clientY - offsetY;
    }
    setCurrentMouseX(x);
    setCurrentMouseY(y);

    if (!isDrawing) {
      return;
    }
    if (snapshotImage.getSnapshot()) {
      ctxRef.current.putImageData(snapshotImage.getSnapshot(), 0, 0);
    }
    let pos = {
      nativeEvent: {
        offsetX: x,
        offsetY: y
      }
    }
    if (currentTool === "Normal") {
      drawBrush(pos, ctxRef, prevMouseX, prevMouseY);
    } else if (currentTool === "Eraser") {
      drawEraser(pos, ctxRef, prevMouseX, prevMouseY);
    } else if (currentTool === "BsSquare") {
      drawRect(pos, ctxRef, prevMouseX, prevMouseY);
    } else if (currentTool === "BsCircle") {
      drawCircle(pos, ctxRef, prevMouseX, prevMouseY);
    } else if (currentTool === "BsTriangle") {
      drawTriangle(pos, ctxRef, prevMouseX, prevMouseY);
    } else if (currentTool === "BsSlashLg") {
      drawLine(pos, ctxRef, prevMouseX, prevMouseY);
    }
  };

  const endDrawing = () => {
    snapshotImage.addSnapshot(
      ctxRef.current.getImageData(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      )
    );
    setIsDrawing(false);
  };

  const areaOut = () => {
    setIsDrawing(false);
    setAreaFocus(false);
  };

  const areaOver = () => {
    setAreaFocus(true);
  };

  // Prevent default touch behavior on the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.addEventListener("touchstart", (e) => e.preventDefault(), false);
    canvas.addEventListener("touchend", (e) => e.preventDefault(), false);
    canvas.addEventListener("touchmove", (e) => e.preventDefault(), false);
  }, []);

  return (
    <>
      <div className='relative w-full h-full min-h-[calc(100vh-250px)] xl:min-h-full xl:max-h-[900px] rounded-xl border-black border-[2px] flex flex-col justify-center items-center bg-[url("assets/image/painter-bg.png")] bg-cover max-sm2:-order-1'>
        <img
          src={DownBtn}
          alt="DownBtn"
          className="absolute -bottom-8 sm2:-bottom-10 right-[36px] sm2:right-[72px] w-[60px] sm2:w-[90px] cursor-pointer disableDrag z-10"
          onClick={() => downloadClick(canvasRef)}
        />
        <canvas
          onMouseDown={startDrawing}
          onMouseUp={endDrawing}
          onMouseMove={draw}
          onMouseOut={areaOut}
          onMouseOver={areaOver}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={endDrawing}
          ref={canvasRef}
          className={`${areaFocus ? "cursor-none" : ""} absolute z-[0]`}
        />
        <CustomCursor />
      </div>
    </>
  );
}

export default DrawingArea;
